import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { X } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { PILLAR_LABEL_BY_SLUG } from "@/lib/pillars";

const PILLARS = ["spirituality", "work", "activities", "sharing"];

const CATEGORIES = [
  { slug: "yoga", label: "Yoga" },
  { slug: "working-space", label: "Working Space" },
  { slug: "wildlife", label: "Wildlife" },
  { slug: "local-experience", label: "Local Experience" },
  { slug: "expert-skill-sharing", label: "Expert/Skill Sharing" },
  { slug: "community-connection", label: "Community Connection" },
];
const PERSONA_OPTIONS = ["yoga", "digital-nomad", "artist", "birder", "founder", "adventure", "wellness", "spiritual-seeker", "creative", "photographer", "naturalist", "backpacker", "maker", "remote-work"];
const FACILITY_PRESETS = [
  "Wi-Fi throughout", "Farm-to-table kitchen", "Yoga shala", "Meditation space",
  "Swimming pool", "River/lake access", "Bonfire evenings", "Library / reading nook",
  "Co-working space", "Bicycle rental", "Ayurveda / spa", "On-site parking",
  "Airport transfer", "Vegetarian / vegan meals", "Laundry service", "Pet friendly",
];

const FOCUS_TAG_PRESETS = [
  "meditation", "silence", "pranayama", "breathwork", "chanting", "kirtan",
  "farming", "permaculture", "seed-saving", "composting", "cooking",
  "asana", "philosophy", "yin",
  "cycling", "trekking", "birding", "swimming", "forest-bathing",
  "pottery", "woodworking", "storytelling", "music", "writing", "photography",
  "deep-work", "founder-community",
  "ayurveda", "massage", "sound-healing",
  "coffee", "brewing",
  "digital-detox", "off-grid",
];
const YOGA_STYLE_PRESETS = ["Hatha", "Ashtanga", "Vinyasa", "Kundalini", "Iyengar", "Yin", "Restorative"];
const MEDITATION_PRESETS = ["Vipassana", "Mindfulness", "Silent", "Guided", "Mantra", "Loving-kindness"];
const DIETARY_PRESETS = ["Vegetarian", "Vegan", "Sattvic", "Ayurvedic", "Gluten-free", "Jain"];

const SECTION_NAV = [
  { id: "sec-property", label: "1 · Property" },
  { id: "sec-story", label: "2 · Host Story" },
  { id: "sec-wellness", label: "3 · Wellness" },
  { id: "sec-rooms", label: "4 · Rooms" },
  { id: "sec-packages", label: "5 · Packages" },
  { id: "sec-pillar-pricing", label: "6 · Pillar Pricing" },
];

const DEFAULT_IMAGES = [
  "https://images.pexels.com/photos/17059187/pexels-photo-17059187.jpeg",
  "https://images.pexels.com/photos/13539139/pexels-photo-13539139.jpeg",
];

export default function CreateExperience() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [form, setForm] = useState({
    title: "", tagline: "", description: "",
    location: "", state: "", country: "", pin_code: "", address: "",
    pillars: [], persona_tags: [], categories: [],
    images: [DEFAULT_IMAGES[0]],
    activities: [""],
    included: [""],
    facilities: [],
    place_intro: "",
    community_connect: [],
    transformation: { philosophy: "", promise: "", not_for: "", depth: {}, focus_tags: [] },
    wellness: { yoga_styles: [], meditation_types: [], dietary: [], practitioners: [], medical_notes: "" },
    rooms: [],
    packages: [],
    add_ons: [],
    pillar_pricing: { yoga_fee: 0, learning_fee: 0, sharing_mode: "volunteer", sharing_masterclass_fee: 0, discount_percent: 0 },
  });
  const [saving, setSaving] = useState(false);
  // Raw string for the "custom facilities" input — kept separate so mid-word spaces aren't stripped on every keystroke.
  const [customFacRaw, setCustomFacRaw] = useState("");

  const togglePillar = (p) => setForm(f => ({
    ...f,
    pillars: f.pillars.includes(p) ? f.pillars.filter(x => x !== p) : [...f.pillars, p],
  }));
  const togglePersona = (p) => setForm(f => ({
    ...f,
    persona_tags: f.persona_tags.includes(p) ? f.persona_tags.filter(x => x !== p) : [...f.persona_tags, p],
  }));
  const toggleCategory = (c) => setForm(f => ({
    ...f,
    categories: (f.categories || []).includes(c) ? (f.categories || []).filter(x => x !== c) : [...(f.categories || []), c],
  }));
  const updateList = (field, i, v) => setForm(f => {
    const arr = [...f[field]]; arr[i] = v; return { ...f, [field]: arr };
  });
  const addToList = (field) => setForm(f => ({ ...f, [field]: [...f[field], ""] }));
  const removeFromList = (field, i) => setForm(f => ({ ...f, [field]: f[field].filter((_, idx) => idx !== i) }));

  // Rooms / Packages / Add-ons helpers
  const updateNested = (field, i, patch) => setForm(f => {
    const arr = [...f[field]]; arr[i] = { ...arr[i], ...patch }; return { ...f, [field]: arr };
  });
  const removeNested = (field, i) => setForm(f => ({ ...f, [field]: f[field].filter((_, idx) => idx !== i) }));
  // Generate a stable client-side ID for freshly-added form rows so React keys don't rely on array index.
  const tmpId = () => `tmp_${Math.random().toString(36).slice(2, 10)}`;
  const addRoom = () => setForm(f => ({ ...f, rooms: [...f.rooms, { _rid: tmpId(), name: "", description: "", capacity: 2, amenities: [], rate_type: "per_night", rate_usd: 50, quantity: 1 }] }));
  const addPackage = () => setForm(f => ({ ...f, packages: [...f.packages, { _rid: tmpId(), name: "", description: "", pillars: [], activities: [], duration_days: 7, price_usd: 200 }] }));
  const addAddOn = () => setForm(f => ({ ...f, add_ons: [...f.add_ons, { _rid: tmpId(), name: "", description: "", price_usd: 20, unit: "per_person" }] }));
  const addCommunityConnect = () => setForm(f => ({ ...f, community_connect: [...f.community_connect, { _rid: tmpId(), title: "", description: "" }] }));

  const toggleFacility = (name) => setForm(f => ({
    ...f,
    facilities: f.facilities.includes(name) ? f.facilities.filter(x => x !== name) : [...f.facilities, name],
  }));

  // Transformation profile
  const setTransform = (patch) => setForm(f => ({ ...f, transformation: { ...(f.transformation || {}), ...patch } }));
  const setPillarDepth = (pillar, val) => setForm(f => ({
    ...f,
    transformation: {
      ...(f.transformation || {}),
      depth: { ...((f.transformation || {}).depth || {}), [pillar]: val },
    },
  }));
  const toggleFocusTag = (tag) => setForm(f => {
    const cur = (f.transformation?.focus_tags) || [];
    return { ...f, transformation: { ...(f.transformation || {}), focus_tags: cur.includes(tag) ? cur.filter(x => x !== tag) : [...cur, tag] } };
  });

  // Wellness helpers
  const setWellness = (patch) => setForm(f => ({ ...f, wellness: { ...(f.wellness || {}), ...patch } }));
  const toggleWellnessList = (field, val) => setForm(f => {
    const cur = (f.wellness?.[field]) || [];
    return { ...f, wellness: { ...(f.wellness || {}), [field]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] } };
  });
  const addPractitioner = () => setForm(f => ({ ...f, wellness: { ...(f.wellness || {}), practitioners: [...((f.wellness || {}).practitioners || []), { _rid: tmpId(), name: "", credentials: "" }] } }));
  const updatePractitioner = (i, patch) => setForm(f => {
    const arr = [...((f.wellness || {}).practitioners || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...f, wellness: { ...(f.wellness || {}), practitioners: arr } };
  });
  const removePractitioner = (i) => setForm(f => ({ ...f, wellness: { ...(f.wellness || {}), practitioners: ((f.wellness || {}).practitioners || []).filter((_, idx) => idx !== i) } }));

  const togglePackagePillar = (i, p) => setForm(f => {
    const arr = [...f.packages];
    const cur = arr[i].pillars || [];
    arr[i] = { ...arr[i], pillars: cur.includes(p) ? cur.filter(x => x !== p) : [...cur, p] };
    return { ...f, packages: arr };
  });

  // Load existing experience when editing
  useEffect(() => {
    if (!editId) return;
    api.get(`/experiences/${editId}`).then(({ data }) => {
      setForm({
        title: data.title || "",
        tagline: data.tagline || "",
        description: data.description || "",
        location: data.location || "",
        state: data.state || "",
        country: data.country || "",
        pin_code: data.pin_code || "",
        address: data.address || "",
        pillars: data.pillars || [],
        persona_tags: data.persona_tags || [],
        categories: data.categories || [],
        images: data.images?.length ? data.images : [DEFAULT_IMAGES[0]],
        activities: data.activities?.length ? data.activities : [""],
        included: data.included?.length ? data.included : [""],
        facilities: data.facilities || [],
        place_intro: data.place_intro || "",
        community_connect: (data.community_connect || []).map((c) => ({ ...c, _rid: c._rid || tmpId() })),
        transformation: data.transformation || { philosophy: "", promise: "", not_for: "", depth: {}, focus_tags: [] },
        wellness: data.wellness
          ? { ...data.wellness, practitioners: (data.wellness.practitioners || []).map((p) => ({ ...p, _rid: p._rid || tmpId() })) }
          : { yoga_styles: [], meditation_types: [], dietary: [], practitioners: [], medical_notes: "" },
        rooms: (data.rooms || []).map((r) => ({ ...r, _rid: r._rid || r.id || tmpId() })),
        packages: (data.packages || []).map((p) => ({ ...p, _rid: p._rid || p.id || tmpId() })),
        add_ons: (data.add_ons || []).map((a) => ({ ...a, _rid: a._rid || a.id || tmpId() })),
        pillar_pricing: data.pillar_pricing || { yoga_fee: 0, learning_fee: 0, sharing_mode: "volunteer", sharing_masterclass_fee: 0, discount_percent: 0 },
      });
      // Seed the raw custom-facilities input with whatever isn't a preset
      const customFacs = (data.facilities || []).filter(f => !FACILITY_PRESETS.includes(f));
      setCustomFacRaw(customFacs.join(", "));
    }).catch((err) => {
      console.error("Load experience failed", err);
      toast.error("Could not load this listing");
      nav("/host");
    });
  }, [editId, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.pillars.length === 0) { toast.error("Pick at least one pillar"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        activities: form.activities.filter(Boolean),
        included: form.included.filter(Boolean),
        images: form.images.filter(Boolean),
        rooms: form.rooms
          .filter(r => r.name)
          .map(({ _rid, ...r }) => ({
            ...r,
            capacity: parseInt(r.capacity) || 1,
            rate_usd: parseFloat(r.rate_usd) || 0,
            quantity: parseInt(r.quantity) || 1,
            amenities: (r.amenities || []).filter(Boolean),
          })),
        packages: form.packages
          .filter(p => p.name)
          .map(({ _rid, ...p }) => ({
            ...p,
            duration_days: parseInt(p.duration_days) || 1,
            price_usd: parseFloat(p.price_usd) || 0,
            activities: (p.activities || []).filter(Boolean),
          })),
        add_ons: form.add_ons
          .filter(a => a.name)
          .map(({ _rid, ...a }) => ({ ...a, price_usd: parseFloat(a.price_usd) || 0 })),
        pillar_pricing: {
          yoga_fee: parseFloat(form.pillar_pricing?.yoga_fee) || 0,
          learning_fee: parseFloat(form.pillar_pricing?.learning_fee) || 0,
          sharing_mode: form.pillar_pricing?.sharing_mode || "volunteer",
          sharing_masterclass_fee: parseFloat(form.pillar_pricing?.sharing_masterclass_fee) || 0,
          discount_percent: Math.max(0, Math.min(100, parseFloat(form.pillar_pricing?.discount_percent) || 0)),
        },
        facilities: (form.facilities || []).filter(Boolean),
        place_intro: form.place_intro || "",
        community_connect: (form.community_connect || [])
          .filter(c => c.title)
          .map(({ _rid, ...c }) => ({ ...c, description: c.description || "" })),
        transformation: {
          philosophy: form.transformation?.philosophy || "",
          promise: form.transformation?.promise || "",
          not_for: form.transformation?.not_for || "",
          depth: form.transformation?.depth || {},
          focus_tags: (form.transformation?.focus_tags || []).filter(Boolean),
        },
        wellness: {
          yoga_styles: (form.wellness?.yoga_styles || []).filter(Boolean),
          meditation_types: (form.wellness?.meditation_types || []).filter(Boolean),
          dietary: (form.wellness?.dietary || []).filter(Boolean),
          practitioners: (form.wellness?.practitioners || []).filter(p => p.name).map(({ _rid, ...p }) => p),
          medical_notes: form.wellness?.medical_notes || "",
        },
        published: true,
      };
      const { data } = isEdit
        ? await api.put(`/experiences/${editId}`, payload)
        : await api.post("/experiences", payload);
      toast.success(isEdit ? "Listing updated — under review" : "Experience published");
      nav(`/experience/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || (isEdit ? "Failed to save changes" : "Failed to publish"));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F2EC]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
        <div className="overline">{isEdit ? "Edit listing" : "New listing"}</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-1">
          {isEdit ? <>Refine your <span className="italic text-moss">space</span>.</> : <>Share your <span className="italic text-moss">space</span>.</>}
        </h1>

        {/* Section navigator */}
        <nav className="mt-6 sticky top-3 z-20 bg-canvas/95 backdrop-blur border border-subtle rounded-full px-3 py-2 flex flex-wrap gap-1 items-center" data-testid="section-nav">
          {SECTION_NAV.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`nav-${s.id}`}
              className="text-xs px-3 py-1.5 rounded-full text-muted2 hover:text-ink hover:bg-subtle transition-colors"
            >
              {s.label}
            </a>
          ))}
        </nav>

        <form onSubmit={submit} className="mt-10 space-y-6 bg-white border border-subtle rounded-2xl p-8">
          <section id="sec-property" className="scroll-mt-24">
          <div className="overline mb-1">Section 1 · Property details</div>
          <p className="text-xs text-muted2 mb-4">Basics that appear at the top of your listing.</p>
          <div>
            <Label>Title</Label>
            <Input data-testid="exp-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required className="rounded-xl bg-white border-subtle" />
          </div>
          <div>
            <Label>Tagline (one poetic line)</Label>
            <Input data-testid="exp-tagline" value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} required className="rounded-xl bg-white border-subtle" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea data-testid="exp-description" rows={5} value={form.description} onChange={e => setForm({...form, description: e.target.value})} required className="rounded-xl bg-white border-subtle" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Place (city / town)</Label>
              <Input data-testid="exp-location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} required className="rounded-xl bg-white border-subtle" placeholder="Rishikesh" />
            </div>
            <div>
              <Label>State / Province</Label>
              <Input data-testid="exp-state" value={form.state} onChange={e => setForm({...form, state: e.target.value})} className="rounded-xl bg-white border-subtle" placeholder="Uttarakhand" />
            </div>
            <div>
              <Label>Country</Label>
              <Input data-testid="exp-country" value={form.country} onChange={e => setForm({...form, country: e.target.value})} required className="rounded-xl bg-white border-subtle" placeholder="India" />
            </div>
            <div>
              <Label>Pin / ZIP code</Label>
              <Input data-testid="exp-pin-code" value={form.pin_code} onChange={e => setForm({...form, pin_code: e.target.value})} className="rounded-xl bg-white border-subtle" placeholder="249137" />
            </div>
            <div className="md:col-span-2">
              <Label>Exact location / Address</Label>
              <Input data-testid="exp-address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="rounded-xl bg-white border-subtle" placeholder="Bhoomi Retreats, Tapovan, Laxman Jhula Road" />
              <p className="text-[10px] text-muted2 mt-1">Shown to travelers only after their booking is confirmed.</p>
            </div>
          </div>

          {/* Facilities & amenities (property-wide) */}
          <div className="border-t border-subtle pt-6">
            <Label className="text-base">Facilities & amenities on the property</Label>
            <p className="text-xs text-muted2 mb-3">Tap what applies. These are shown as chips on your listing.</p>
            <div className="flex flex-wrap gap-1.5">
              {FACILITY_PRESETS.map(fac => (
                <button
                  key={fac}
                  type="button"
                  data-testid={`facility-toggle-${fac.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  onClick={() => toggleFacility(fac)}
                  className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                    form.facilities.includes(fac) ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                  }`}
                >
                  {fac}
                </button>
              ))}
            </div>
            <div className="mt-3">
              <Label className="text-xs">Anything else? (comma separated custom facilities)</Label>
              <Input
                data-testid="facilities-custom"
                placeholder="Rooftop terrace, Sauna, Fireplace…"
                value={customFacRaw}
                onChange={e => {
                  const raw = e.target.value;
                  setCustomFacRaw(raw);
                  const preset = form.facilities.filter(f => FACILITY_PRESETS.includes(f));
                  // Store trimmed non-empty entries in form.facilities for submit; the raw string with spaces stays in local state.
                  const custom = raw.split(",").map(s => s.trim()).filter(Boolean);
                  setForm(f => ({ ...f, facilities: [...preset, ...custom] }));
                }}
                className="rounded-lg bg-white border-subtle"
              />
            </div>
          </div>

          {/* About the place & community */}
          <div className="border-t border-subtle pt-6">
            <Label className="text-base">About the place & community</Label>
            <p className="text-xs text-muted2 mb-3">A short intro to the surrounding place — its people, culture, and rhythm. Helps travelers feel oriented before they arrive.</p>
            <Textarea
              data-testid="exp-place-intro"
              rows={5}
              value={form.place_intro}
              onChange={e => setForm({ ...form, place_intro: e.target.value })}
              placeholder="A quiet village on the banks of the Ganga, home to families who have been welcoming pilgrims for generations. Mornings smell of woodsmoke and marigold; afternoons belong to river birds…"
              className="rounded-xl bg-white border-subtle"
            />
          </div>

          {/* Community connect */}
          <div className="border-t border-subtle pt-6">
            <Label className="text-base">Connect with the community</Label>
            <p className="text-xs text-muted2 mb-3">Opportunities travelers can join to share and learn from local people — sharing circles, skill exchanges, farm days, workshops.</p>
            {form.community_connect.map((c, i) => (
              <div key={c._rid || i} data-testid={`cc-editor-${i}`} className="bg-canvas border border-subtle rounded-xl p-4 mt-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <Input
                    data-testid={`cc-title-${i}`}
                    placeholder="e.g. Weekly sharing circle with village elders"
                    value={c.title}
                    onChange={e => updateNested("community_connect", i, { title: e.target.value })}
                    className="rounded-lg bg-white border-subtle"
                  />
                  <button type="button" data-testid={`remove-cc-${i}`} onClick={() => removeNested("community_connect", i)} className="text-muted2 hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
                <Textarea
                  data-testid={`cc-description-${i}`}
                  rows={2}
                  placeholder="What travelers can expect — who they'll meet, what they'll do, what they'll offer or learn."
                  value={c.description}
                  onChange={e => updateNested("community_connect", i, { description: e.target.value })}
                  className="rounded-lg bg-white border-subtle"
                />
              </div>
            ))}
            <button type="button" data-testid="add-cc" onClick={addCommunityConnect} className="text-sm text-moss mt-3 underline">+ Add a connect option</button>
          </div>

          <div>
            <Label>Pillars (select any combination)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {PILLARS.map(p => (
                <button key={p} type="button" data-testid={`pillar-toggle-${p}`}
                  onClick={() => togglePillar(p)}
                  className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                    form.pillars.includes(p) ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                  }`}>
                  {PILLAR_LABEL_BY_SLUG[p] || p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Categories (help travelers find you on Discover)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {CATEGORIES.map(c => (
                <button key={c.slug} type="button" data-testid={`category-toggle-${c.slug}`}
                  aria-pressed={(form.categories || []).includes(c.slug)}
                  onClick={() => toggleCategory(c.slug)}
                  className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                    (form.categories || []).includes(c.slug) ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                  }`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Persona tags</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {PERSONA_OPTIONS.map(p => (
                <button key={p} type="button" onClick={() => togglePersona(p)}
                  className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                    form.persona_tags.includes(p) ? "bg-terracotta text-canvas border-terracotta" : "bg-white border-subtle hover:border-terracotta"
                  }`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Images</Label>
            <p className="text-xs text-muted2 mb-2">Upload your own photos (max 8MB each) — the first will be your cover.</p>
            <ImageUploader images={form.images.filter(Boolean)} onChange={(imgs) => setForm({...form, images: imgs.length ? imgs : [""] })} />
          </div>

          <div>
            <Label>Activities</Label>
            {form.activities.map((a, i) => (
              <div key={i} className="flex gap-2 mt-2">
                <Input value={a} onChange={e => updateList("activities", i, e.target.value)} className="rounded-xl bg-white border-subtle" />
                {form.activities.length > 1 && <button type="button" onClick={() => removeFromList("activities", i)}><X className="h-4 w-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={() => addToList("activities")} className="text-sm text-moss mt-2 underline">+ Add activity</button>
          </div>

          <div>
            <Label>What's included</Label>
            {form.included.map((a, i) => (
              <div key={i} className="flex gap-2 mt-2">
                <Input value={a} onChange={e => updateList("included", i, e.target.value)} className="rounded-xl bg-white border-subtle" />
                {form.included.length > 1 && <button type="button" onClick={() => removeFromList("included", i)}><X className="h-4 w-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={() => addToList("included")} className="text-sm text-moss mt-2 underline">+ Add inclusion</button>
          </div>

          </section>

          {/* Section 2 - Host Story / Transformation Profile */}
          <section id="sec-story" className="scroll-mt-24 pt-8 border-t border-subtle">
            <div className="overline mb-1">Section 2 · Host story</div>
            <p className="text-xs text-muted2 mb-4">The soul of your listing. This is what powers our matching engine — the more honestly you fill this, the better we match you to the right travelers.</p>

            <Label>Your hosting philosophy</Label>
            <Textarea
              data-testid="transform-philosophy"
              rows={4}
              value={form.transformation?.philosophy || ""}
              onChange={(e) => setTransform({ philosophy: e.target.value })}
              placeholder="Why do you host? What do you believe travelers actually need?"
              className="rounded-xl bg-white border-subtle mt-1"
            />

            <div className="mt-4">
              <Label>The transformation you promise</Label>
              <Textarea
                data-testid="transform-promise"
                rows={3}
                value={form.transformation?.promise || ""}
                onChange={(e) => setTransform({ promise: e.target.value })}
                placeholder="What will change in a traveler by the last day?"
                className="rounded-xl bg-white border-subtle mt-1"
              />
            </div>

            <div className="mt-4">
              <Label>Who this is NOT for</Label>
              <Textarea
                data-testid="transform-not-for"
                rows={2}
                value={form.transformation?.not_for || ""}
                onChange={(e) => setTransform({ not_for: e.target.value })}
                placeholder="Honesty here saves everyone disappointment."
                className="rounded-xl bg-white border-subtle mt-1"
              />
            </div>

            <div className="mt-6">
              <Label>Depth on each pillar (1 = light touch, 5 = deeply immersive)</Label>
              <div className="mt-3 space-y-3">
                {PILLARS.map((p) => (
                  <div key={p} className="flex items-center gap-4">
                    <div className="w-32 text-sm">{PILLAR_LABEL_BY_SLUG[p] || p}</div>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((n) => {
                        const val = form.transformation?.depth?.[p] || 0;
                        return (
                          <button
                            key={n}
                            type="button"
                            data-testid={`depth-${p}-${n}`}
                            onClick={() => setPillarDepth(p, val === n ? 0 : n)}
                            className={`w-8 h-8 rounded-full border text-xs font-medium transition-colors ${
                              val >= n ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                            }`}
                          >{n}</button>
                        );
                      })}
                      {form.transformation?.depth?.[p] ? (
                        <button type="button" onClick={() => setPillarDepth(p, 0)} className="text-xs text-muted2 ml-2 underline">reset</button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Label>Focus tags (what makes your listing findable)</Label>
              <p className="text-xs text-muted2 mb-2">Tap all that genuinely apply. Travelers filter and match on these.</p>
              <div className="flex flex-wrap gap-1.5">
                {FOCUS_TAG_PRESETS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    data-testid={`focus-tag-${tag}`}
                    onClick={() => toggleFocusTag(tag)}
                    className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                      (form.transformation?.focus_tags || []).includes(tag) ? "bg-terracotta text-canvas border-terracotta" : "bg-white border-subtle hover:border-terracotta"
                    }`}
                  >{tag}</button>
                ))}
              </div>
            </div>
          </section>

          {/* Section 3 - Wellness */}
          <section id="sec-wellness" className="scroll-mt-24 pt-8 border-t border-subtle">
            <div className="overline mb-1">Section 3 · Wellness</div>
            <p className="text-xs text-muted2 mb-4">The practical wellness offering. Helps travelers with specific practices or dietary needs find you.</p>

            <div>
              <Label>Yoga styles offered</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {YOGA_STYLE_PRESETS.map((s) => (
                  <button key={s} type="button" data-testid={`yoga-${s}`}
                    onClick={() => toggleWellnessList("yoga_styles", s)}
                    className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                      (form.wellness?.yoga_styles || []).includes(s) ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <Label>Meditation types</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {MEDITATION_PRESETS.map((s) => (
                  <button key={s} type="button" data-testid={`med-${s}`}
                    onClick={() => toggleWellnessList("meditation_types", s)}
                    className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                      (form.wellness?.meditation_types || []).includes(s) ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <Label>Dietary options</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {DIETARY_PRESETS.map((s) => (
                  <button key={s} type="button" data-testid={`diet-${s}`}
                    onClick={() => toggleWellnessList("dietary", s)}
                    className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                      (form.wellness?.dietary || []).includes(s) ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <Label>On-site practitioners</Label>
              {(form.wellness?.practitioners || []).map((p, i) => (
                <div key={p._rid || i} data-testid={`practitioner-${i}`} className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-start">
                  <Input data-testid={`practitioner-name-${i}`} value={p.name} onChange={(e) => updatePractitioner(i, { name: e.target.value })} placeholder="Name" className="rounded-lg bg-white border-subtle" />
                  <Input data-testid={`practitioner-cred-${i}`} value={p.credentials} onChange={(e) => updatePractitioner(i, { credentials: e.target.value })} placeholder="Credentials / role" className="rounded-lg bg-white border-subtle" />
                  <button type="button" data-testid={`remove-practitioner-${i}`} onClick={() => removePractitioner(i)} className="text-muted2 hover:text-destructive p-2"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <button type="button" data-testid="add-practitioner" onClick={addPractitioner} className="text-sm text-moss mt-2 underline">+ Add practitioner</button>
            </div>

            <div className="mt-4">
              <Label>Medical / accessibility notes</Label>
              <Textarea
                data-testid="wellness-medical"
                rows={3}
                value={form.wellness?.medical_notes || ""}
                onChange={(e) => setWellness({ medical_notes: e.target.value })}
                placeholder="Altitude, terrain, seasonal allergens, contraindicated practices — anything travelers should know."
                className="rounded-xl bg-white border-subtle mt-1"
              />
            </div>
          </section>

          {/* Section 4 - Rooms */}
          <section id="sec-rooms" className="scroll-mt-24 pt-8 border-t border-subtle">
          <div className="overline mb-1">Section 4 · Rooms & cottages</div>
            <p className="text-xs text-muted2 mb-3">Add each accommodation type — travelers pick one at booking. Each has its own tariff and amenities.</p>
            {form.rooms.map((r, i) => (
              <div key={r._rid || r.id || i} data-testid={`room-editor-${i}`} className="bg-canvas border border-subtle rounded-xl p-4 mt-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <Input data-testid={`room-name-${i}`} placeholder="e.g. Garden Cottage" value={r.name} onChange={e => updateNested("rooms", i, { name: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  <button type="button" data-testid={`remove-room-${i}`} onClick={() => removeNested("rooms", i)} className="text-muted2 hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
                <Input placeholder="Short description" value={r.description} onChange={e => updateNested("rooms", i, { description: e.target.value })} className="rounded-lg bg-white border-subtle" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Capacity</Label>
                    <Input type="number" min="1" value={r.capacity} onChange={e => updateNested("rooms", i, { capacity: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  </div>
                  <div>
                    <Label className="text-xs">Quantity</Label>
                    <Input type="number" min="1" value={r.quantity} onChange={e => updateNested("rooms", i, { quantity: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  </div>
                  <div>
                    <Label className="text-xs">Tariff (₹)</Label>
                    <Input data-testid={`room-rate-${i}`} type="number" min="0" step="0.01" value={r.rate_usd} onChange={e => updateNested("rooms", i, { rate_usd: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  </div>
                  <div>
                    <Label className="text-xs">Tariff type</Label>
                    <select
                      data-testid={`room-rate-type-${i}`}
                      value={r.rate_type}
                      onChange={e => updateNested("rooms", i, { rate_type: e.target.value })}
                      className="w-full rounded-lg bg-white border border-subtle px-3 py-2 text-sm"
                    >
                      <option value="per_night">Per night</option>
                      <option value="flat">Flat / full stay</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Amenities (comma separated)</Label>
                  <Input
                    placeholder="Private bathroom, Wi-Fi, Balcony…"
                    value={(r.amenities || []).join(", ")}
                    onChange={e => updateNested("rooms", i, { amenities: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                    className="rounded-lg bg-white border-subtle"
                  />
                </div>
              </div>
            ))}
            <button type="button" data-testid="add-room" onClick={addRoom} className="text-sm text-moss mt-3 underline">+ Add a room / cottage</button>
          </section>

          {/* Section 5 - Packages */}
          <section id="sec-packages" className="scroll-mt-24 pt-8 border-t border-subtle">
          <div className="overline mb-1">Section 5 · Experience packages & add-ons</div>
          <div>
            <Label className="text-base">Experience packages</Label>
            <p className="text-xs text-muted2 mb-3">Curate one or more packages — each blends yoga, work, activities, and sharing however you like.</p>
            {form.packages.map((p, i) => (
              <div key={p._rid || p.id || i} data-testid={`package-editor-${i}`} className="bg-canvas border border-subtle rounded-xl p-4 mt-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <Input data-testid={`package-name-${i}`} placeholder="e.g. 7-Day Yoga Retreat" value={p.name} onChange={e => updateNested("packages", i, { name: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  <button type="button" data-testid={`remove-package-${i}`} onClick={() => removeNested("packages", i)} className="text-muted2 hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
                <Input placeholder="Short description" value={p.description} onChange={e => updateNested("packages", i, { description: e.target.value })} className="rounded-lg bg-white border-subtle" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Duration (days)</Label>
                    <Input type="number" min="1" value={p.duration_days} onChange={e => updateNested("packages", i, { duration_days: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  </div>
                  <div>
                    <Label className="text-xs">Price per guest (₹)</Label>
                    <Input data-testid={`package-price-${i}`} type="number" min="0" step="0.01" value={p.price_usd} onChange={e => updateNested("packages", i, { price_usd: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Pillars included</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {PILLARS.map(pl => (
                      <button
                        key={pl}
                        type="button"
                        data-testid={`package-${i}-pillar-${pl}`}
                        onClick={() => togglePackagePillar(i, pl)}
                        className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                          (p.pillars || []).includes(pl) ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                        }`}
                      >
                        {PILLAR_LABEL_BY_SLUG[pl] || pl}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Included activities (comma separated)</Label>
                  <Input
                    placeholder="Morning yoga, Sunset walks, Cooking class…"
                    value={(p.activities || []).join(", ")}
                    onChange={e => updateNested("packages", i, { activities: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                    className="rounded-lg bg-white border-subtle"
                  />
                </div>
              </div>
            ))}
            <button type="button" data-testid="add-package" onClick={addPackage} className="text-sm text-moss mt-3 underline">+ Add a package</button>
          </div>

          {/* Add-ons */}
          <div className="border-t border-subtle pt-6">
            <Label className="text-base">Optional add-ons</Label>
            <p className="text-xs text-muted2 mb-3">Extras a traveler can toggle at checkout — massages, transfers, workshops, upgrades.</p>
            {form.add_ons.map((a, i) => (
              <div key={a._rid || a.id || i} data-testid={`addon-editor-${i}`} className="bg-canvas border border-subtle rounded-xl p-4 mt-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <Input data-testid={`addon-name-${i}`} placeholder="e.g. Ayurveda massage" value={a.name} onChange={e => updateNested("add_ons", i, { name: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  <button type="button" data-testid={`remove-addon-${i}`} onClick={() => removeNested("add_ons", i)} className="text-muted2 hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
                <Input placeholder="Short description" value={a.description} onChange={e => updateNested("add_ons", i, { description: e.target.value })} className="rounded-lg bg-white border-subtle" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Price (₹)</Label>
                    <Input data-testid={`addon-price-${i}`} type="number" min="0" step="0.01" value={a.price_usd} onChange={e => updateNested("add_ons", i, { price_usd: e.target.value })} className="rounded-lg bg-white border-subtle" />
                  </div>
                  <div>
                    <Label className="text-xs">Charged</Label>
                    <select
                      data-testid={`addon-unit-${i}`}
                      value={a.unit}
                      onChange={e => updateNested("add_ons", i, { unit: e.target.value })}
                      className="w-full rounded-lg bg-white border border-subtle px-3 py-2 text-sm"
                    >
                      <option value="per_person">Per guest</option>
                      <option value="per_night">Per night</option>
                      <option value="flat">Flat / one-time</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" data-testid="add-addon" onClick={addAddOn} className="text-sm text-moss mt-3 underline">+ Add an add-on</button>
          </div>
          </section>

          {/* Section 6 - Pillar Pricing */}
          <section id="sec-pillar-pricing" className="scroll-mt-24 pt-8 border-t border-subtle">
            <div className="overline mb-1">Section 6 · Pillar pricing</div>
            <p className="text-xs text-muted2 mb-4">
              Travelers can custom-build their journey by toggling pillars at checkout. Set the fees you charge for each pillar. <span className="text-moss">Working</span> is always free (labour exchange). <span className="text-moss">Sharing</span> can be a free volunteer contribution or a paid masterclass.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-canvas border border-subtle rounded-xl p-4">
                <div className="font-serif text-lg">Yoga <span className="text-xs text-muted2 font-sans">(spirituality)</span></div>
                <p className="text-[11px] text-muted2 mt-0.5">One-time fee added to the booking when the traveler opts in to your yoga programme.</p>
                <Label className="text-xs mt-3 block">Yoga fee (₹, one-time per booking)</Label>
                <Input
                  data-testid="pillar-yoga-fee"
                  type="number" min="0" step="0.01"
                  value={form.pillar_pricing?.yoga_fee ?? 0}
                  onChange={e => setForm(f => ({ ...f, pillar_pricing: { ...(f.pillar_pricing || {}), yoga_fee: e.target.value } }))}
                  className="rounded-lg bg-white border-subtle"
                />
              </div>

              <div className="bg-canvas border border-subtle rounded-xl p-4">
                <div className="font-serif text-lg">Learning <span className="text-xs text-muted2 font-sans">(activities)</span></div>
                <p className="text-[11px] text-muted2 mt-0.5">One-time fee for guided activities, workshops, or curated learning tracks.</p>
                <Label className="text-xs mt-3 block">Learning fee (₹, one-time per booking)</Label>
                <Input
                  data-testid="pillar-learning-fee"
                  type="number" min="0" step="0.01"
                  value={form.pillar_pricing?.learning_fee ?? 0}
                  onChange={e => setForm(f => ({ ...f, pillar_pricing: { ...(f.pillar_pricing || {}), learning_fee: e.target.value } }))}
                  className="rounded-lg bg-white border-subtle"
                />
              </div>

              <div className="bg-canvas border border-subtle rounded-xl p-4 opacity-90">
                <div className="font-serif text-lg">Working <span className="text-xs text-muted2 font-sans">(work / labour exchange)</span></div>
                <p className="text-[11px] text-muted2 mt-0.5">Travelers exchange time on-site (farming, hosting, building, remote-work co-hosting). Always <span className="font-medium text-moss">free</span> — no fee is charged.</p>
                <div className="mt-3 rounded-lg border border-dashed border-subtle bg-white/70 px-3 py-2 text-xs text-muted2" data-testid="pillar-working-note">₹0 · non-negotiable</div>
              </div>

              <div className="bg-canvas border border-subtle rounded-xl p-4">
                <div className="font-serif text-lg">Sharing <span className="text-xs text-muted2 font-sans">(workshops / skill share)</span></div>
                <p className="text-[11px] text-muted2 mt-0.5">Choose how sharing works at your place.</p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    data-testid="pillar-sharing-mode-volunteer"
                    onClick={() => setForm(f => ({ ...f, pillar_pricing: { ...(f.pillar_pricing || {}), sharing_mode: "volunteer" } }))}
                    className={`flex-1 rounded-full px-3 py-1.5 text-xs border transition-colors ${
                      (form.pillar_pricing?.sharing_mode || "volunteer") === "volunteer"
                        ? "bg-moss text-canvas border-moss"
                        : "bg-white border-subtle hover:border-moss"
                    }`}
                  >Volunteer · Free</button>
                  <button
                    type="button"
                    data-testid="pillar-sharing-mode-masterclass"
                    onClick={() => setForm(f => ({ ...f, pillar_pricing: { ...(f.pillar_pricing || {}), sharing_mode: "masterclass" } }))}
                    className={`flex-1 rounded-full px-3 py-1.5 text-xs border transition-colors ${
                      form.pillar_pricing?.sharing_mode === "masterclass"
                        ? "bg-terracotta text-canvas border-terracotta"
                        : "bg-white border-subtle hover:border-terracotta"
                    }`}
                  >Paid masterclass</button>
                </div>
                {form.pillar_pricing?.sharing_mode === "masterclass" && (
                  <div className="mt-3">
                    <Label className="text-xs">Masterclass fee (₹, one-time per booking)</Label>
                    <Input
                      data-testid="pillar-sharing-masterclass-fee"
                      type="number" min="0" step="0.01"
                      value={form.pillar_pricing?.sharing_masterclass_fee ?? 0}
                      onChange={e => setForm(f => ({ ...f, pillar_pricing: { ...(f.pillar_pricing || {}), sharing_masterclass_fee: e.target.value } }))}
                      className="rounded-lg bg-white border-subtle"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Host discount (%) applied to the traveler's final total */}
            <div className="mt-6 bg-canvas border border-subtle rounded-xl p-4">
              <div className="font-serif text-lg">Final discount</div>
              <p className="text-[11px] text-muted2 mt-0.5">Optional percentage discount applied on the traveler's final total (rooms + packages + add-ons + pillars). Leave at 0 for no discount.</p>
              <Label className="text-xs mt-3 block">Discount (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="pillar-discount-percent"
                  type="number" min="0" max="100" step="1"
                  value={form.pillar_pricing?.discount_percent ?? 0}
                  onChange={e => setForm(f => ({ ...f, pillar_pricing: { ...(f.pillar_pricing || {}), discount_percent: e.target.value } }))}
                  className="rounded-lg bg-white border-subtle max-w-[120px]"
                />
                <span className="text-sm text-muted2">% off the final total</span>
              </div>
            </div>
          </section>

          <Button data-testid="publish-experience" type="submit" disabled={saving} className="w-full rounded-full bg-moss hover:bg-moss-hover text-canvas py-6">
            {saving ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save changes" : "Publish experience")}
          </Button>
        </form>
      </div>
      <Footer />
    </div>
  );
}
